import type { PhotoKind } from "@agw/contracts";
import { ApiError } from "./api";
import { api } from "./endpoints";
import type { UploadType } from "./image";

export type PutBlob = (url: string, blob: Blob, headers: Record<string, string>, onProgress?: (fraction: number) => void, signal?: AbortSignal) => Promise<void>;

/** PUT straight to the presigned storage URL. XHR because fetch has no upload progress. */
export const xhrPut: PutBlob = (url, blob, headers, onProgress, signal) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const lost = () => reject(new ApiError(0, "network", "No connection. The photo will upload when you have signal."));
    xhr.open("PUT", url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    if (!Object.keys(headers).some((k) => k.toLowerCase() === "content-type")) xhr.setRequestHeader("content-type", blob.type);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new ApiError(xhr.status, "upload", `Photo upload failed (${xhr.status})`)));
    xhr.onerror = lost;
    xhr.ontimeout = lost;
    xhr.onabort = lost; // the phone dropped it, or we gave up on a stalled upload; either way, try again later
    signal?.addEventListener("abort", () => xhr.abort(), { once: true });
    xhr.send(blob);
  });

export interface PhotoUploader {
  createPhotoUpload: typeof api.createPhotoUpload;
  putBlob: PutBlob;
}

export const defaultUploader: PhotoUploader = { createPhotoUpload: api.createPhotoUpload, putBlob: xhrPut };

/**
 * Two steps: ask the API for a staging URL, then upload the bytes. Returns the photoId for the form.
 * The preview goes after the photo; if it fails the claim simply shows the full photo instead.
 */
export async function uploadPhoto(
  blob: Blob,
  contentType: UploadType,
  kind: PhotoKind,
  onProgress?: (fraction: number) => void,
  uploader: PhotoUploader = defaultUploader,
  signal?: AbortSignal,
  thumb?: Blob,
): Promise<string> {
  const slot = await uploader.createPhotoUpload({ contentType, sizeBytes: blob.size, kind, ...(thumb ? { thumbSizeBytes: thumb.size } : {}) });
  if (signal?.aborted) throw new ApiError(0, "network", "Upload stopped.");
  await uploader.putBlob(slot.uploadUrl, blob, slot.headers, onProgress, signal);
  if (thumb && slot.thumb) await uploader.putBlob(slot.thumb.uploadUrl, thumb, slot.thumb.headers, undefined, signal).catch(() => undefined);
  onProgress?.(1);
  return slot.photoId;
}
